import { useGetRequestsQuery, useUpdateRequestMutation } from "api/TenantSlug/Request/hooks";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import dayjs from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useRoleLabel } from "hooks/useRoleLabel";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { UserRequestStatus } from "types/enums";

import { ApproveRejectButtons } from "../shared/ActionChips";
import { useOptimisticHide } from "../shared/hooks/useOptimisticHide";
import { SearchableTable } from "../shared/SearchableTable";
import { ApiErr, UpdateRequestVariables } from "../shared/types";
import { renderDateSmart, syncDayjsLocale } from "../shared/utils/dateLocale";

import UserRequestData = App.Data.UserRequestData;
import OfferData = App.Data.OfferData;

export const OfferRequests: React.FC = () => {
    const { t, i18n } = usePaldockTranslation("ui.dashboard.cards.offer-requests");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const roleLabel = useRoleLabel();

    useEffect(() => {
        syncDayjsLocale(i18n.language);
    }, [i18n.language]);

    const query = useGetRequestsQuery(
        {
            page: {
                number: 1,
                size: 5,
            },
            filter: {
                filter: { type: "offer", status: "pending" },
                sort: "-created_at",
                include: "user,offer",
            },
        },
        {
            staleTime: 5000,
        }
    );

    const { hide, unhide, filterVisible } = useOptimisticHide();

    const updateRequestMutation = useUpdateRequestMutation({
        onSuccess: async () => {
            await query.refetch();
            toast.success(t("messages.update_success"));
        },
        onError: (_err: ApiErr, variables: UpdateRequestVariables) => {
            unhide(variables.requestId);
            toast.error(t("messages.update_error"));
        },
    });

    const updateStatus = (id: string, status: UserRequestStatus) => {
        hide(id);
        updateRequestMutation.mutate({ requestId: id, request: { status } });
    };

    const rows = useMemo(
        () => filterVisible<UserRequestData>(query.data?.user_requests, "pending"),
        [query.data?.user_requests, filterVisible]
    );

    return (
        <SearchableTable<UserRequestData>
            rows={rows}
            loading={query.isLoading || query.isRefetching}
            rowKey={(r) => r.id}
            allowSearch={false}
            searchKeys={[
                (r) => (r.created_at ? dayjs(r.created_at).format("D.M.YYYY HH:mm") : ""),
                (r) => (r.created_at ? dayjs(r.created_at).format("D.M.YYYY") : ""),
                (r) => r.user?.nickname ?? "",
                (r) => (r.offer as OfferData)?.title ?? "",
                (r) => roleLabel(r?.data?.roles?.[0]) ?? "",
            ]}
            columns={[
                {
                    title: tG("date"),
                    dataIndex: "created_at",
                    render: (v: string) => (
                        <span title={v ? dayjs(v).format("D.M.YYYY HH:mm") : ""}>{renderDateSmart(v)}</span>
                    ),
                },
                {
                    title: tG("user"),
                    dataIndex: ["user", "nickname"],
                    render: (_: unknown, r: UserRequestData) => <EntityLinkCell.Owner entity={r.user} mode="drawer" />,
                },
                {
                    title: tG("offer"),
                    dataIndex: ["offer", "title"],
                    render: (_: unknown, r: UserRequestData) => (
                        <EntityLinkCell.Offer entity={r.offer as OfferData} offerTarget="auto" />
                    ),
                },
                {
                    title: tG("type"),
                    dataIndex: "type",
                    render: (_: unknown, r: UserRequestData) => roleLabel(r?.data?.roles?.[0]),
                },
                // { title: "Country", dataIndex: ["country", "code"] },
                {
                    title: tG("actions"),
                    key: "actions",
                    align: "center",
                    width: 120,
                    render: (_: unknown, r: UserRequestData) => (
                        <ApproveRejectButtons
                            state={r.status}
                            pendingValue="pending"
                            approvedValue="accepted"
                            rejectedValue="rejected"
                            labels={{
                                approve: tC("accept"),
                                reject: tC("reject"),
                            }}
                            loading={updateRequestMutation.isPending}
                            onApprove={() => updateStatus(r.id, UserRequestStatus.Accepted)}
                            onReject={() => updateStatus(r.id, UserRequestStatus.Rejected)}
                        />
                    ),
                },
            ]}
        />
    );
};
